import { Injectable } from '@angular/core';
import { ToolsControlService } from './tools-control.service';
import olMap from 'ol/Map';
import { EventsKey } from 'ol/events';
import { Chart, Interaction, InteractionModeFunction } from 'chart.js';
import { getRelativePosition } from 'chart.js/helpers';
import { unByKey } from 'ol/Observable';
import { Draw } from 'ol/interaction';
import VectorSource from 'ol/source/Vector';

import { PointsService } from './points.service';
import { MapService } from './map.service';
import { SearchItem } from '../models/searchItem';
import { SearchService } from './search.service';
import { CalculationsService } from './calculations.service';
import { CalculationSettingsModel } from '../models/configuration';
import { ConfigurationService } from './configuration.service';
import { Feature } from 'ol';
import { CrossSectionDTO, CrossSectionResultModel } from '../models/CrossSectionModel';
import { UtilsService } from './utils.service';
import { Fill, Stroke, Style } from 'ol/style';
import { GlobalSelectionControlService } from '../map/services/global-selection-control.service';
import Point from 'ol/geom/Point';
import { FeatureType } from '../models/EnumTypes';
import OlFeature from 'ol/Feature';
import { Coordinate } from 'ol/coordinate';
import * as olProj from 'ol/proj';
import CircleStyle from 'ol/style/Circle';
import { CoordenateService } from './coordenate.service';


declare module 'chart.js' {
  interface InteractionModeMap {
    myCustomModeCrossSection: InteractionModeFunction;
  }
}

@Injectable({
  providedIn: 'root'
})

export class CrossSectionService {

  // Window
  active: boolean;
  fullScreenMode: boolean = false;
  loading: boolean = false;

  // Graph
  ctx: HTMLCanvasElement;
  lineOnChart = null;
  chart: Chart;

  map: olMap;
  eventKey: EventsKey;
  draw: Draw;
  feature: Feature<any>;

  unitsInMeters: boolean = false;

  constructor(
    private toolsService: ToolsControlService,
    private pointsController: PointsService,
    private mapService: MapService,
    private searchService: SearchService,
    private calculationService: CalculationsService,
    private configurationService: ConfigurationService,
    private utilsService: UtilsService,
    private globalSelectionService: GlobalSelectionControlService,

    private rcsControl: CoordenateService
  ) {

    this.map = this.mapService.getMap('map');
  }

  openWindow(): void {

    if (this.configurationService.currentConfiguration?.display?.horizontalUnits != undefined) {
      this.unitsInMeters = this.configurationService.currentConfiguration?.display?.horizontalUnits === 'm';
    }

    this.active = true;
    this.toolsService.navigationOnTop = true;

    // Create graph
    this.createCrossSectionGraph();

    // Add draw interaction
    this.manageDrawInteraction();
  }

  cancelWindow(): void {
    this.active = !this.active;
    this.loading = false;
    this.feature = null;
    this.toolsService.navigationOnTop = false;
    this.cleanFeatures();
    //unByKey(this.eventKey);
    this.map.removeInteraction(this.draw);
    this.draw = null;
    this.chart?.destroy();
    this.chart.options.interaction = null;
  }

  hideWindow(): void {
    this.active = !this.active;
    this.loading = false;
    this.toolsService.navigationOnTop = false;
    this.map.removeInteraction(this.draw);

  }

  createCrossSectionGraph() {

    this.chart?.destroy();
      // Interaction
    Interaction.modes.myCustomModeCrossSection = function (chart, e, options, useFinalPosition) {
      const position = getRelativePosition(e, chart);
      const items = [];
      Interaction.evaluateInteractionItems(chart, 'x', position, (element, datasetIndex, index) => {
        if (element.inXRange(position.x, useFinalPosition)) {

          const dataX = chart.scales.x.getValueForPixel(position.x);
          if (dataX >= 0) {
            if (position['type'] === 'mousemove') {
              this.drawTrackPoint(dataX);
            }

            items.push({ element, datasetIndex, index });
          }
        }
      });
      return items;

    }.bind(this);

    // Draw vertical line in the graph
    const thisObject = this;
    const drawVerticalLinePlugins = {
      id: 'verticalLine',
      afterEvent(chart, args, pluginsOptions) {
        const xCursor = args.event.x;
        const click = args.event.type;
        if (click === 'mousemove' && thisObject.chart.data.datasets.length > 0) {

          const dataX = chart.scales.x.getValueForPixel(xCursor);
          if (dataX >= 0) {
            thisObject.drawChartLine(xCursor);
          }
        }
      },
    }

    // Init chart
    this.chart = new Chart(this.ctx, {
      type: 'line',
      data: null,
      plugins: [drawVerticalLinePlugins],
      options: {
        responsive: true,
        plugins: {
          legend: {
            display: true
          },
          tooltip: {
            mode: 'nearest',
            displayColors: true,
            filter: function (tooltipItem) {
              return tooltipItem.datasetIndex === 0;
            },
            callbacks: {
              title: function (context) {
                return "Distance: " + context[0].parsed.x;
              },
              label: function (context) {
                let label = context.dataset.label || '';

                if (label) {
                  label += ': ';
                }
                if (context.parsed.y !== null) {
                  label += "Terrain Height: " + context.parsed.y;
                }
                return label;
              },
            }
          },
        },
        interaction: {
          mode: 'myCustomModeCrossSection',
          intersect: false
        },
        scales: {
          x: {
            grid: { display: false },
            type: 'linear',
            stacked: false,
            title: {
              display: true,
              text: 'Distance ' + (this.unitsInMeters ? '(m)' : '(NM)'),
            },
            ticks: {
              stepSize: 0.05,
              includeBounds: false
            },
            min: 0,
            max: 1.05
          },
          y: {
            grid: { display: false },
            stacked: false,
            title: {
              display: true,
              text: 'Terrain Height (m)'
            },
            ticks: {
              includeBounds: false,
              stepSize: 0.25
            },
            min: null,
            max: null
          }
        },
        elements: {
          line: {
            borderWidth: 1,
            borderColor: 'rgb(0,0,0)'
          },
          point: {
            radius: 0
          }
        }
      }
    });


  }

  manageDrawInteraction() {
    if (this.draw != null) {
      this.map.removeInteraction(this.draw);
    }

    if (this.mapService.draw != null) {
      this.map.removeInteraction(this.mapService.draw);
      this.map.removeInteraction(this.mapService.select);
      this.mapService.draw = null;
    }

    const source = new VectorSource();
    this.draw = new Draw({
      source: source,
      type: "LineString",
      maxPoints: 2,
    });

    this.map.addInteraction(this.draw);

    let context = this;
    this.draw.on('drawend', function (endEvent) {
      if (context.loading == false) {
        context.loading = true;

        context.cleanFeatures();

        // Get the coordinates of the line
        context.feature = endEvent.feature;
        context.addFeatureToMap();

        context.map.removeInteraction(context.draw);
        context.draw = null;

        // Remove interaction
        context.performCrossSection();
      }
    });

  }

  addFeatureToMap() {

    if (this.feature != null) {
      this.globalSelectionService.crossSectionLayers.active = true;

      let source = this.pointsController.crossSectionLayer.childLayers[0].layers
        .find((element) => element.id === 'Cross_Section_Layer')
        .olLayer.getSource();

      source.addFeature(this.feature);
    }
  }

  cleanFeatures() {

    if (this.globalSelectionService.crossSectionLayers.active == true) {
      this.globalSelectionService.crossSectionLayers.active = false;

      this.pointsController.crossSectionLayer.childLayers?.forEach(el => {
        el.layers.forEach(layer => {
          layer.olLayer.getSource().clear();
        })
      });
    }

  }
  reproject(sourceProjection: string){
    this.feature?.getGeometry().transform(sourceProjection,this.map.getView().getProjection().getCode());
  }
  performCrossSection() {

    // Set the data
    let crossSectionDTO: CrossSectionDTO = new CrossSectionDTO();
    crossSectionDTO.calculationSettings = CalculationSettingsModel.mapToCalculation(this.configurationService.currentConfiguration);
    crossSectionDTO.calculationSettings.geometricConstraints = true;


    let coordinates = [];

    this.feature.getGeometry().getCoordinates().forEach(coord => {

      let target: Coordinate = [coord[0], coord[1]];
      let coordinate: Coordinate = olProj.toLonLat(target,this.rcsControl.activeRCS.isactive? this.rcsControl.activeRCS.epsg : "EPSG:3857");
      coordinates.push(coordinate);

    });
    crossSectionDTO.coordinates = coordinates;

    try {
      this.calculationService.calculateCrossSection(crossSectionDTO).subscribe(
        async (results: CrossSectionResultModel[]) => {

          // Refresh data
          this.refreshCrossSection(results);
          this.loading = false;

          this.manageDrawInteraction();

        }, (error) => {
          this.calculationService.showError(error)
          this.loading = false;

          this.manageDrawInteraction();
        }
      );

    } catch (e) {
      this.loading = false;
    }
  }

  refreshCrossSection(results: CrossSectionResultModel[]) {

    var dataset = [];
    var step = null;

    var minY: number = null;
    var maxY: number = null;
    var maxX: number = null;

    var end = 0;
    var lastIndex = 0;

    if (results != null) {

      var j = 1;
      var meters = 0;
      var labels = [];

      for (let result of results) {

        if (result != null && result.terrainHeightsArray != null) {

          step = result.interpolationStep;
          var values = result.terrainHeightsArray;
          var seriesPoints = [];
          var pointData = [];

          values.forEach((height, index) => {
            seriesPoints.push(lastIndex + index, height);

            if ((minY == null) || (height < minY)) {
              minY = height;
            }

            if ((maxY == null) || (height > maxY)) {
              maxY = height;
            }

            meters = end + index * step;
            var label = meters;

            if (!this.unitsInMeters) {
              label = this.utilsService.metersToNm(end + index * step);
            }

            labels.push(label);

            if ((maxX == null) || (label > maxX)) {
              maxX = label;
            }

            pointData.push({ x: label, y: height });
          });

          end = meters;

          dataset.push({
            data: pointData,
            label: "Section " + j + " - [" + pointData[0].y + ", " + pointData[pointData.length - 1].y + "]",
            fill: false,
            borderColor: "#3cba9f",
          });

          j++;
        }
      }
    }

    const data = {
      labels: labels,
      datasets: dataset,
    };

    this.chart.data = data;

    if (minY === 0 && maxY === 0) {
      this.chart.options.scales.y.min = -1;
      this.chart.options.scales.y.max = 1;
    } else {
      this.chart.options.scales.y.min = minY;
      this.chart.options.scales.y.max = maxY;
    }

    this.chart.options.scales.x.max = maxX;
    this.chart.update();

  }

  drawTrackPoint(position: number) {

    let source = this.pointsController.crossSectionLayer.childLayers[0].layers
      .find((element) => element.id === 'Cross_Section_Layer')
      .olLayer.getSource();
    let feature = source.getFeatureById("trackPoint");
    if (feature != null) {
      source.removeFeature(feature);
    }

    const lineCoordinates = this.feature.getGeometry().getCoordinates();
    const lineInitial = lineCoordinates[0]
    const lineFinal = lineCoordinates[1]

    const finalValue = parseFloat(this.chart.data.labels[this.chart.data.labels.length - 1].toString())

    const percentagePosition = position * 100 / finalValue
    const pointX = lineInitial[0] + ((lineFinal[0] - lineInitial[0]) * (percentagePosition / 100))
    const pointY = lineInitial[1] + ((lineFinal[1] - lineInitial[1]) * (percentagePosition / 100))


    let coordinate: Coordinate = [pointX, pointY];

    // Creating point
    let point = new OlFeature({
      geometry: new Point(coordinate),
      featureType: FeatureType.CrossSection
    });
    point.setId("trackPoint")

    let color = 'rgba(255, 0, 0, 0.5)';

    let style = new Style({
      image: new CircleStyle({
        radius: 3,
        fill: new Fill({
          color: color
        })
      })
    });
    point.setStyle(style);
    source.addFeature(point);
  }


  drawChartLine(position: number) {

    const { ctx, chartArea: { top, bottom } } = this.chart;
    class Line {
      width: any;
      constructor(xCoor) {
        this.width = xCoor;
      };
      draw(ctx) {
        this.width = this.width;
        ctx.restore();
        ctx.beginPath();
        ctx.lineWidth = 2;
        ctx.strokeStyle = 'rgba(0,0,0) ';
        ctx.moveTo(this.width, top);
        ctx.lineTo(this.width, bottom);
        ctx.stroke();
        ctx.save();
      }
    }

    if (position != null && this.chart.data.datasets.length > 0) {
      let drawLine = new Line(position);
      drawLine.draw(ctx);
    }
  }

}


